import { ISortDirections, SortDirections, Spinner, Text, Box } from '@color/continuum';
import { useUrlQueryParams } from '@color/lib';
import {
  DataGrid,
  GridColDef,
  GridRenderCellParams,
  GridRowParams,
  GridSortModel,
} from '@mui/x-data-grid';
import { useMemo } from 'react';
import { generatePath } from 'react-router-dom';

import { FixedLoadingIndicator } from '../../common/compoments/FixedLoadingIndicator';
import { useVariantClassificationListData } from '../apis';
import { VARIANT_CLASSIFICATION_DETAIL_PATH } from '../paths';
import { IVariantClassificationListParams, VariantClassificationState } from '../types';

import { AutoRefreshInterval } from './AutoRefreshSwitch';
import {
  DEFAULT_VARIANT_CLASSIFICATION_SEARCH_PARAMS,
  VariantClassificationField,
} from './constants';
import { formatVariantClassification } from './utils';

const formatValue = (value: unknown): string | number | boolean => {
  if (typeof value === 'object') {
    // Can't format arbitrary objects, though we shouldn't be getting them in
    // the first place. But this helps typescript feel safe.
    return 'Error: unexpected value object';
  }
  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
    return value || '-';
  }
  return '-';
};

const renderCell = (params: GridRenderCellParams) => <Text>{formatValue(params.value)}</Text>;

const renderList = (items: string[]) => {
  // Values are keyed by their occurrence count rather than array index, since some
  // columns (e.g. EFFECT) can contain duplicate values after splitting/sorting.
  const occurrenceCounts = new Map<string, number>();
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 0.25,
        minWidth: 0,
        maxWidth: '100%',
      }}
    >
      {items.map((val) => {
        const occurrence = occurrenceCounts.get(val) ?? 0;
        occurrenceCounts.set(val, occurrence + 1);
        return (
          <Text
            key={`${val}-${occurrence}`}
            style={{
              whiteSpace: 'normal',
              wordBreak: 'break-all',
              overflowWrap: 'break-word',
              minWidth: 0,
              maxWidth: '100%',
            }}
          >
            {val}
          </Text>
        );
      })}
    </Box>
  );
};

// These columns always surface regardless of state
const COLUMNS: GridColDef[] = [
  {
    field: VariantClassificationField.GENE,
    headerName: 'Gene',
    flex: 1,
    renderCell,
  },
  {
    field: VariantClassificationField.VARIANT,
    headerName: 'Variant',
    minWidth: 300,
    flex: 2,
    renderCell: (params: GridRenderCellParams) => renderList(params.value),
  },
  {
    field: VariantClassificationField.EFFECT,
    headerName: 'Effect',
    flex: 1.75,
    renderCell: (params: GridRenderCellParams) =>
      // Some effects are split by commas w/o spaces, some with spaces (we support old and new formats). Let's render them as a list
      renderList(
        params.value
          .split(',')
          .map((effect) => effect.trim())
          .sort()
      ),
  },
  {
    field: VariantClassificationField.EXON,
    headerName: 'Exon',
    flex: 1,
    sortable: false,
    renderCell,
  },
  {
    field: VariantClassificationField.CLASSIFICATION,
    headerName: 'Classification',
    flex: 1.25,
    sortable: false,
    renderCell,
  },
];

const formatColumns = (columns: GridColDef[], state: string, readyForReview: string) => {
  switch (state) {
    case VariantClassificationState.NEW:
      return [
        {
          field: VariantClassificationField.CREATED,
          headerName: 'Created',
          flex: 1.25,
          renderCell,
        },
        ...columns,
      ];
    case VariantClassificationState.REREVIEW:
      return [
        {
          field: VariantClassificationField.LAST_REVIEWED,
          headerName: 'Last Reviewed',
          flex: 1.25,
          renderCell,
        },
        ...columns,
      ];
    case VariantClassificationState.IN_PROGRESS:
      return [
        {
          field: VariantClassificationField.UPDATED,
          headerName: 'Updated',
          flex: 1.25,
          renderCell,
        },
        ...columns,
      ];
    case VariantClassificationState.INTERPRETED:
      return [
        {
          field: VariantClassificationField.INTERPRETED,
          headerName: 'Interpreted',
          flex: 1.25,
          renderCell,
        },
        ...columns,
      ];
    case VariantClassificationState.REVIEWED:
      return [
        {
          field: VariantClassificationField.REVIEWED,
          headerName: 'Reviewed',
          flex: 1.25,
          renderCell,
        },
        ...(readyForReview === ''
          ? columns
          : [
              ...columns,
              {
                field: VariantClassificationField.MEDIAN_ALLELE_FRACTION,
                headerName: 'Median Allele Fraction',
                description: 'Median of the last 20 calls',
                minWidth: 230,
                renderCell: (params: GridRenderCellParams) => {
                  const { median, low_fraction_count } = params.value;
                  if (median === null) {
                    return '';
                  }
                  return (
                    <Text
                      sx={(theme) =>
                        low_fraction_count > 0 ? { color: theme.palette.error.main } : {}
                      }
                    >
                      {median.toFixed(2)} ({low_fraction_count} {'calls <0.3'})
                    </Text>
                  );
                },
              },
            ]),
      ];
    default:
      return [
        {
          field: VariantClassificationField.CREATED,
          headerName: 'Created',
          flex: 1.25,
          renderCell,
        },
        ...columns,
        {
          field: VariantClassificationField.STATE,
          headerName: 'State',
          flex: 1,
          renderCell,
        },
      ];
  }
};

export const VariantClassificationDataGrid = ({
  autoRefreshInterval = AutoRefreshInterval.Off,
}: {
  autoRefreshInterval: AutoRefreshInterval;
}) => {
  const { queryParams, setQueryParams } = useUrlQueryParams<keyof IVariantClassificationListParams>(
    DEFAULT_VARIANT_CLASSIFICATION_SEARCH_PARAMS
  );

  const {
    state,
    clinicalDomain,
    variants,
    classification,
    kitBarcode,
    batchId,
    nomenclature,
    gene,
    vepConsequence,
    exon,
    readyForReview,
    orderBy,
    orderDirection,
  } = queryParams;

  const initialSortDirections: SortDirections = useMemo(() => {
    const validFields = new Set(Object.values(VariantClassificationField));
    if (!validFields.has(orderBy as VariantClassificationField)) return {};
    if (!['asc', 'desc'].includes(orderDirection)) return {};
    return { [orderBy]: orderDirection } as SortDirections;
  }, [orderBy, orderDirection]);

  const { variantClassificationResponse, variantClassificationTableData } =
    useVariantClassificationListData(
      {
        state,
        clinicalDomain,
        variants,
        classification,
        kitBarcode,
        batchId,
        nomenclature,
        gene,
        vepConsequence,
        exon,
        readyForReview,
        orderBy,
        orderDirection,
      },
      initialSortDirections,
      autoRefreshInterval === AutoRefreshInterval.Off ? undefined : autoRefreshInterval
    );

  const { data, isLoading, isFetching, paginationOptions, tableOptions } =
    variantClassificationResponse;

  const { setSortDirections: setDjangoSortDirections, sortDirections: djangoSortDirections } =
    tableOptions;
  const { rowsPerPage, setRowsPerPage, pageNumber, setPageNumber } = paginationOptions;

  const sortModel = useMemo<GridSortModel>(
    () =>
      Object.entries(djangoSortDirections).map(([field, sort]) => ({
        field,
        sort: sort || undefined,
      })),
    [djangoSortDirections]
  );

  const onSortModelChange = (newModel: GridSortModel) => {
    const direction: ISortDirections = {};
    // Convert from GridSortDirection of ['asc', 'desc', null, undefined] to MUI SortDirections which are 'asc' | 'desc' | false
    // and coerce null or undefined to false. See TableCell API at https://mui.com/material-ui/api/table-cell/ for definitions
    newModel.forEach((gridSortItem) => {
      direction[gridSortItem.field] = gridSortItem.sort || false;
    });
    setDjangoSortDirections(direction);

    // If a column is sorted, update the query params to reflect the new sort direction
    if (newModel.length > 0) {
      const { field, sort } = newModel[0];
      setQueryParams({ ...queryParams, orderBy: field, orderDirection: sort || '' });
    } else {
      setQueryParams({ ...queryParams, orderBy: undefined, orderDirection: undefined });
    }
  };

  if (isLoading) {
    return <Spinner />;
  }

  return (
    <>
      {isFetching && <FixedLoadingIndicator />}

      <DataGrid
        sx={(theme) => ({
          '& .MuiDataGrid-columnHeader': {
            color: theme.typography.body1.color,
            fontSize: theme.typography.body1.fontSize,
            fontFamily: theme.typography.fontFamily,
            backgroundColor: theme.palette.background.lightBg,
          },
          '& .MuiDataGrid-cell': {
            display: 'flex',
            alignItems: 'center',
            whiteSpace: 'normal',
            paddingTop: 0.5,
            paddingBottom: 0.5,
            borderBottom: `1px solid ${theme.palette.background.mediumBg}`,
          },
        })}
        autoHeight
        getRowHeight={() => 'auto'}
        rows={variantClassificationTableData.map(formatVariantClassification)}
        columns={formatColumns(COLUMNS, state, readyForReview)}
        rowCount={data?.count || 0}
        disableColumnMenu
        pagination
        paginationModel={{ page: pageNumber, pageSize: rowsPerPage }}
        paginationMode="server"
        onPaginationModelChange={(model) => {
          if (model.page !== pageNumber) {
            setPageNumber(model.page);
          }
          if (model.pageSize !== rowsPerPage) {
            setRowsPerPage(model.pageSize);
          }
        }}
        sortingMode="server"
        sortModel={sortModel}
        onSortModelChange={onSortModelChange}
        onRowClick={(rowData: GridRowParams) => {
          window.open(
            generatePath(VARIANT_CLASSIFICATION_DETAIL_PATH, { id: rowData.id }),
            '_blank'
          );
        }}
      />
    </>
  );
};
